import { lazy, Suspense, useCallback, useRef, useState } from 'react';

import type { PostErrorResponse } from 'services/idm';

import { useAppTranslation } from 'hooks/use-translates';

import { CodeRequestButton } from '@login/components/code-request-button';
import SixDigitsCodeForm, {
	type SixDigitsCodeFormHandle,
} from '@login/components/six-digits-code-form';

import styles from './verification-form.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));

type VerificationFormProps = {
	onResendCode: () => void;
	onSubmit: (code: string) => Promise<void>;
};

const CODE_LENGTH = 6;

export const VerificationForm = ({
	onResendCode,
	onSubmit,
}: VerificationFormProps) => {
	const { t } = useAppTranslation();

	// The code lives only here: it was previously mirrored into a hidden
	// react-hook-form field as well, giving two sources of truth for one value.
	const [verificationCode, setVerificationCode] = useState('');
	const [triggerRenew, setTriggerRenew] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const codeFormRef = useRef<SixDigitsCodeFormHandle>(null);

	const handleVerify = useCallback(
		async (event: React.FormEvent) => {
			event.preventDefault();
			if (isSubmitting) return;

			if (verificationCode.length !== CODE_LENGTH) {
				codeFormRef.current?.showErrorMessage(
					t('verification.code_required')
				);
				return;
			}

			setIsSubmitting(true);
			try {
				await onSubmit(verificationCode);
			} catch (e) {
				const error = e as PostErrorResponse;

				if (
					error.status === 422 &&
					error.title === 'VALIDATION_ERROR' &&
					error.errors
				) {
					const message =
						error.errors.email?.[0] ??
						error.errors.code?.[0] ??
						error.errors.mobile?.[0];

					if (message) codeFormRef.current?.showErrorMessage(message);
				}
			} finally {
				codeFormRef.current?.reset();
				setIsSubmitting(false);
			}
		},
		[isSubmitting, verificationCode, onSubmit, t]
	);

	const handleResend = useCallback(() => {
		setTriggerRenew((previous) => !previous);
		codeFormRef.current?.reset();
		onResendCode();
	}, [onResendCode]);

	return (
		<Suspense>
			<form
				onSubmit={handleVerify}
				id="form-profile"
				className={styles['verify-email-form']}
				noValidate
			>
				<div className={styles['form-box--input']}>
					<p className="label-medium">
						{t('verification.enter_code_label')}
					</p>

					<SixDigitsCodeForm
						outputCode={setVerificationCode}
						ref={codeFormRef}
					/>
				</div>

				<div className={styles['verify-email-form-btns-set']}>
					<Button
						type="submit"
						label={
							isSubmitting
								? t('verification.verifying_button_label')
								: t('verification.submit_button_label')
						}
						variant="secondary"
						fullWidth={true}
						isLoading={isSubmitting}
						dimension="large"
					/>
					<CodeRequestButton
						onClick={handleResend}
						triggerRenew={triggerRenew}
					/>
				</div>
			</form>
		</Suspense>
	);
};

export default VerificationForm;
